<template>
    <ToggleSimple @update:open="toggleOpen = $event">
        <template #toggle>
            <div class="left">
                <div v-if="$te(`fields.${name}.label`)" class="toggle-text">
                    {{ $t(`fields.${name}.label`) }}

                    <span v-if="isActive">
                        <div class="icon-check-dark"></div>
                    </span>
                </div>
                <div v-if="$te(`fields.${name}.description`)" class="toggle-description">
                    {{ $t(`fields.${name}.description`) }}
                </div>
            </div>
            <div class="right">
                <div v-if="isActive" class="reset" @click="resetSection">
                    <div class="icon-reset-pos"></div>
                </div>
                <div class="toggle-icon"></div>
            </div>
        </template>

        <template #content>
            <div :class="cssClassOptions">
                <CustomRangeSimple
                    inputKey="employees_alt"
                    :modelValue="localCompanySize.employees"
                    :min="employeesRange.min"
                    :max="employeesRange.max"
                    :showLabel="true"
                    :showLabelMinMax="true"
                    @update:modelValue="onRangeChanged('employees', $event)"
                    :showDescription="showDescriptions"
                />
                
                <CustomRangeSimple
                    inputKey="revenue_alt"
                    :modelValue="localCompanySize.revenue"
                    :min="revenueRange.min"
                    :max="revenueRange.max"
                    :showLabel="true"
                    :showLabelMinMax="true"
                    @update:modelValue="onRangeChanged('revenue', $event)"
                    :showDescription="showDescriptions"
                />
            </div>
        </template>
    </ToggleSimple>
</template>

<script>
    import debounce from 'lodash/debounce';
    import ToggleSimple from '@/components/elements/misc/ToggleSimple';
    import CustomRangeSimple from '@/components/elements/inputs/CustomRangeSimple';

    export default {
        name: "OptionsCompanySize",
        components: {
            ToggleSimple,
            CustomRangeSimple
        },
        props: {
            modelValue: {
                type: Object,
                default: () => ({
                    employees: { min: null, max: null },
                    revenue: { min: null, max: null }
                })
            },
            defaultVal: {
                type: Object,
                default: () => ({
                    employees: { min: null, max: null },
                    revenue: { min: null, max: null }
                })
            },
            showDescriptions: {
                type: Boolean,
                default: true
            },
            cssClassOptions: {
                type: String,
                default: 'options-type-4'
            }
        },
        data() {
            return {
                name: 'company_size',
                toggleOpen: false,
                employeesRange: {
                    min: 0,
                    max: 10000000
                },
                revenueRange: {
                    min: 0,
                    max: 100000000000000
                },
                localCompanySize: {
                    employees: {
                        min: this.modelValue.employees?.min !== null ? this.modelValue.employees.min : '',
                        max: this.modelValue.employees?.max !== null ? this.modelValue.employees.max : ''
                    },
                    revenue: {
                        min: this.modelValue.revenue?.min !== null ? this.modelValue.revenue.min : '',
                        max: this.modelValue.revenue?.max !== null ? this.modelValue.revenue.max : ''
                    }
                }
            };
        },
        computed: {
            isActive() {
                return JSON.stringify(this.modelValue) !== JSON.stringify(this.defaultVal);
            }
        },
        methods: {
            resetSection(event = null) {
                if (event) {
                    event.stopPropagation();
                }
                
                this.localCompanySize = {
                    employees: { min: '', max: '' },
                    revenue: { min: '', max: '' }
                };
                
                this.$emit('update:modelValue', {
                    employees: { min: null, max: null },
                    revenue: { min: null, max: null }
                });
            },
            onRangeChanged(field, value) {
                const { min, max } = value;
                this.localCompanySize[field] = { min, max };
                
                const rangeConfig = field === 'employees' ? this.employeesRange : this.revenueRange;
                
                if ((min === '' || min < 1 || min < rangeConfig.min) && (max === '' || max < 1 || max > rangeConfig.max)) {
                    this.debouncedUpdateRange(field, { min: null, max: null });
                } else {
                    const newRange = {
                        min: min === '' || min < 1 || min < rangeConfig.min ? null : Number(min),
                        max: max === '' || max < 1 || max > rangeConfig.max ? null : Number(max)
                    };
                    
                    this.debouncedUpdateRange(field, newRange);
                }
            },
            debouncedUpdateRange(field, value) {
                const updatedCompanySize = { ...this.modelValue };
                updatedCompanySize[field] = value;
                this.$emit('update:modelValue', updatedCompanySize);
            }
        },
        watch: {
            modelValue: {
                deep: true,
                handler(newVal) {
                    if (newVal) {
                        this.localCompanySize = {
                            employees: {
                                min: newVal.employees?.min !== null ? newVal.employees.min : '',
                                max: newVal.employees?.max !== null ? newVal.employees.max : ''
                            },
                            revenue: {
                                min: newVal.revenue?.min !== null ? newVal.revenue.min : '',
                                max: newVal.revenue?.max !== null ? newVal.revenue.max : ''
                            }
                        };
                    }
                }
            }
        },
        mounted() {
            this.debouncedUpdateRange = debounce(function(field, value) {
                const updatedCompanySize = { ...this.modelValue };
                updatedCompanySize[field] = value;
                this.$emit('update:modelValue', updatedCompanySize);
            }.bind(this), 300);
        }
    }
</script>